import React, { useState, useEffect, useRef } from "react";
import { Badge, Checkbox, Space, Typography, Row, Col, Card, Table, Progress, Tooltip, Modal, Button, Divider } from "antd";
import dayjs from "dayjs";
import ExportarExcelPoa from "./ExportarExcelPoa";
import { SearchOutlined, FileImageOutlined, FolderOpenOutlined, BarChartOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import ConvencionesAvance from "./ConvencionesAvance";
import AvanceProyectosChart from './Graficos/AvanceProyectosChart';
import AvanceProyectosChartBarras from './Graficos/AvanceProyectosChartBarras';
import html2canvas from "html2canvas";
import config from '../../config';
import "./PoaResumen.css";

const PoaResumen = ({ data }) => {

    const [filteredData, setFilteredData] = useState([]);
    const tableRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
    const [visibleGrafico, setVisibleGrafico] = useState(false);
    const [mostrarFechas, setMostrarFechas] = useState(true);
    const [visibleModal, setVisibleModal] = useState(false);
    const [selectedChart, setSelectedChart] = useState(null);
    const { Text } = Typography;
    const baseUrl = config.baseUrl;
    const obtenerNombreMes = (numeroMes) => {
        const meses = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
        return meses[numeroMes - 1];
    };
    const conversionData = [
        { rango: "≤ 15%", color: "#198754", descripcion: "Cumplimiento cercano" },
        { rango: "15% - 35%", color: "#ffa500", descripcion: "Diferencia moderada" },
        { rango: "> 35%", color: "#f20013", descripcion: "Diferencia crítica" }
    ];
    const toggleFechas = () => {
        setMostrarFechas(!mostrarFechas);
    };

    const getColorPorDiferencia = (programado, ejecutado) => {
        const P = parseFloat(programado) || 0;
        const E = parseFloat(ejecutado) || 0;
        const diff = Math.abs(P - E);
        if (diff <= 15) return "#198754";
        if (diff <= 35) return "#ffa500";
        return "#f20013";
    };

    useEffect(() => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            console.warn("⚠️ No hay datos válidos en `data`.");
            return;
        }
        const nuevosDatos = data.map((proyecto) => {
            if (!proyecto.fecha_corte) {
                console.warn(`⚠️ Proyecto sin fecha_corte:`, proyecto);
                return null;
            }
            const fechaActualizacion = dayjs(proyecto.fecha_corte);
            const mesFiltro = obtenerNombreMes(fechaActualizacion.month() + 1);
            const anoFiltro = fechaActualizacion.year().toString();
            const avanceP = proyecto.P?.find(item => item.ano === anoFiltro && item.mes === mesFiltro);
            const avanceE = proyecto.E?.find(item => item.ano === anoFiltro && item.mes === mesFiltro);
            return {
                key: proyecto.oidProyecto,
                oidProyecto: proyecto.oidProyecto,
                codigo: proyecto.codigo,
                nombreProyecto: proyecto.nombreProyecto,
                fecha_inicio: proyecto.fecha_inicio,
                fecha_terminacion: proyecto.fecha_terminacion,
                fecha_actual_terminacion: proyecto.fecha_actual_terminacion,
                fecha_actualizacion: proyecto.fecha_actualizacion,
                fecha_corte: proyecto.fecha_corte,
                mes: mesFiltro,
                ano: anoFiltro,
                avanceProgramado: avanceP ? avanceP.Total_Avance : "0.000000",
                avanceEjecutado: avanceE ? avanceE.Total_Avance : "0.000000",
            };
        }).filter(Boolean);
        setFilteredData(nuevosDatos);
    }, [data]);

    const verPOA = (record) => {
        if (!record?.codigo) {
            console.error("Código no disponible para ver POA");
            return;
        }
        const url = `${baseUrl}/planOperativo/${record.codigo}`;
        window.open(url, "_blank");
    };

    const verDetalles = (record) => {
        const proyectoCompleto = data.find(p => p.oidProyecto === record.oidProyecto);
        if (!proyectoCompleto) {
            console.warn("⚠️ No se encontró el proyecto completo en los datos originales.");
            return;
        }
        const historialAvances = [];
        if (proyectoCompleto.P) {
            proyectoCompleto.P.forEach(item => {
                historialAvances.push({
                    mes: item.mes,
                    ano: item.ano,
                    avanceProgramado: item.Total_Avance,
                    avanceEjecutado: "0.000000"
                });
            });
        }
        if (proyectoCompleto.E) {
            proyectoCompleto.E.forEach(item => {
                const registroExistente = historialAvances.find(av => av.mes === item.mes && av.ano === item.ano);
                if (registroExistente) {
                    registroExistente.avanceEjecutado = item.Total_Avance;
                } else {
                    historialAvances.push({
                        mes: item.mes,
                        ano: item.ano,
                        avanceProgramado: "0.000000",
                        avanceEjecutado: item.Total_Avance
                    });
                }
            });
        }
        setProyectoSeleccionado({
            ...record,
            historialAvances,
        });

        setVisible(true);
    };

    const cerrarModal = () => {
        setVisible(false);
        setProyectoSeleccionado(null);
    };

    const columns = [
        ...(mostrarFechas
            ? [
                {
                    title: "",
                    key: "detalle",
                    align: "center",
                    render: (_, record) => (
                        <Tooltip title="Ver detalles">
                            <SearchOutlined
                                className="icono-lupa"
                                style={{ fontSize: "18px", color: "#1677ff", cursor: "pointer" }}
                                onClick={() => verDetalles(record)}
                            />
                        </Tooltip>
                    ),
                },
                {
                    title: "",
                    key: "detallePOA",
                    align: "center",
                    render: (_, record) => (
                        <Tooltip title="Ver Plan Operativo del Proyecto">
                            <FolderOpenOutlined
                                className="icono-lupa"
                                style={{ fontSize: "18px", color: "#1677ff", cursor: "pointer" }}
                                onClick={() => verPOA(record)}
                            />
                        </Tooltip>
                    ),
                }
            ]
            : []),

        {
            title: (
                <span>
                    Proyecto &nbsp;
                    <Checkbox checked={mostrarFechas} onChange={toggleFechas} />
                </span>
            ),
            key: "codigoProyecto",
            align: "left",
            render: (text, record) => `${record.codigo} - ${record.nombreProyecto}`,
        },
        ...(mostrarFechas
            ? [
                {
                    title: "Fecha Inicio POA",
                    dataIndex: "fecha_inicio",
                    key: "fecha_inicio",
                    align: "center",
                    render: (text) => text ? dayjs(text).format("DD-MM-YYYY") : "",
                },
                {
                    title: "Fecha Inicial de Terminación POA",
                    dataIndex: "fecha_terminacion",
                    key: "fecha_terminacion",
                    align: "center",
                    render: (text) => text ? dayjs(text).format("DD-MM-YYYY") : "",
                }, {
                    title: "Fecha Actual de Terminación POA",
                    dataIndex: "fecha_actual_terminacion",
                    key: "fecha_actual_terminacion",
                    align: "center",
                    render: (text) => {
                        if (!text || text === "0000-00-00") {
                            return "N/A";
                        }
                        return dayjs(text).format("DD-MM-YYYY");
                    },
                }
                ,
                {
                    title: "Fecha de Corte",
                    dataIndex: "fecha_corte",
                    key: "fecha_corte",
                    align: "center",
                    render: (text) => text ? dayjs(text).format("DD-MM-YYYY") : "",
                },
                {
                    title: "Mes de Corte",
                    key: "mes_ano",
                    align: "center",
                    render: (_, record) => record.mes && record.ano ? `${record.mes}-${record.ano}` : ""
                },
            ]
            : []),
        {
            title: "Avance Programado (P)",
            dataIndex: "avanceProgramado",
            key: "avanceProgramado",
            align: "center",
            render: (text) => (
                <Progress
                    percent={100}
                    format={() => (
                        <span style={{ fontWeight: "bold", fontSize: "14px", color: "#FFF" }}>
                            {`${Math.round(parseFloat(text))}%`}
                        </span>
                    )}
                    percentPosition={{ align: 'right', type: 'inner' }}
                    size={[100, 20]}
                    strokeColor={{ '0%': '#108ee9', '100%': '#108ee9' }}
                />
            )
        },
        {
            title: "Avance Ejecutado (E)",
            dataIndex: "avanceEjecutado",
            key: "avanceEjecutado",
            align: "center",
            render: (text, record) => {

                const ejecutado = parseFloat(text) || 0;
                const programado = parseFloat(record.avanceProgramado) || 0;
                const color = getColorPorDiferencia(programado, ejecutado);

                const porcentajeMostrar =
                    record.oidProyecto === 11
                        ? Math.floor(ejecutado) // Solo P11 -> 99.985359 => 99
                        : Math.round(ejecutado); // Los demás siguen igual

                return (
                    <Progress
                        percent={100}
                        format={() => (
                            <span
                                style={{
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    color: "#FFF",
                                }}
                            >
                                {`${porcentajeMostrar}%`}
                            </span>
                        )}
                        percentPosition={{ align: "right", type: "inner" }}
                        size={[100, 20]}
                        strokeColor={color}
                    />
                );
            }
        }

    ];

    const columnasModal = [
        {
            title: "Mes de Corte",
            key: "mes_ano",
            align: "center",
            render: (_, record) =>
                record.mes && record.ano ? `${record.mes} - ${record.ano}` : ""
        },
        {
            title: "Avance Programado (P)",
            dataIndex: "avanceProgramado",
            key: "avanceProgramado",
            align: "center",
            render: (text) => (
                <Progress
                    percent={100}
                    format={() => (
                        <span style={{ fontWeight: "bold", fontSize: "14px", color: "#FFF" }}>
                            {`${Math.round(parseFloat(text))}%`}
                        </span>
                    )}
                    percentPosition={{ align: 'right', type: 'inner' }}
                    size={[150, 20]}
                    strokeColor={{ '0%': '#108ee9', '100%': '#108ee9' }}
                />
            )
        },
        {
            title: "Avance Ejecutado (E)",
            dataIndex: "avanceEjecutado",
            key: "avanceEjecutado",
            align: "center",
            render: (text, record) => {

                const ejecutado = parseFloat(text) || 0;
                const programado = parseFloat(record.avanceProgramado) || 0;
                const color = getColorPorDiferencia(programado, ejecutado);
                return (
                    <Progress
                        percent={100}
                        format={() => (
                            <span style={{ fontWeight: "bold", fontSize: "14px", color: "#FFF" }}>
                                {`${Math.round(ejecutado)}%`}
                            </span>
                        )}
                        percentPosition={{ align: "right", type: "inner" }}
                        size={[150, 20]}
                        strokeColor={color}
                    />
                );
            }
        }
    ];

    const capturarTabla = async () => {
        if (tableRef.current) {
            const canvas = await html2canvas(tableRef.current);
            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png");
            link.download = "Porcentajes_de_Avance_POAs.png";
            link.click();
        }
    };

    const handleOpenModal = (chartType) => {
        setSelectedChart(chartType);
        setVisibleModal(true);
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>Porcentajes de Avance POA´s</h2>
                <div style={{ display: "flex", gap: 8 }}>
                    {!mostrarFechas ? (
                        <Button type="primary" icon={<FileImageOutlined />} onClick={capturarTabla} style={{ backgroundColor: "#28a745", borderColor: "#218838", color: "white", marginBottom: "10px" }}>
                            Imagen Tabla
                        </Button>
                    ) : null}
                    <ExportarExcelPoa dataSource={filteredData} todosLosDatos={data} />
                    <Button
                        onClick={() => setVisibleGrafico(!visibleGrafico)}
                        type="primary"
                        icon={visibleGrafico ? <EyeInvisibleOutlined /> : <BarChartOutlined />}
                        style={{ backgroundColor: "#28a745", borderColor: "#218838", color: "white" }}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = "#28a745")}
                    >
                        {visibleGrafico ? "Ocultar" : "Mostrar"}
                    </Button>
                    <ConvencionesAvance />
                </div>
            </div>
            {visibleGrafico ? (
                <div className="container-fluid">
                    <div
                        className="row d-flex align-items-stretch"
                        style={{ width: "100%", gap: "2%" }}
                    >
                        <div
                            className="col-12 col-md-6 d-flex flex-column mb-5 pt-2"
                            style={{
                                flex: 1,
                                minHeight: "500px",
                                background: "#fff",
                                borderRadius: "12px",
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                overflow: "hidden",
                                transition: "box-shadow 0.3s ease, transform 0.2s ease",
                                cursor: "pointer"
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.boxShadow = "0px 8px 16px rgba(0, 0, 0, 0.2)";
                                e.currentTarget.style.transform = "translateY(-5px)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.boxShadow = "0px 4px 8px rgba(0, 0, 0, 0.1)";
                                e.currentTarget.style.transform = "translateY(0px)";
                            }}
                        >
                            <Button type="primary" onClick={() => handleOpenModal("barras")} style={{ padding: "5px" }}>
                                Maximizar
                            </Button>
                            <div style={{ flex: 1, display: "flex" }}>
                                <div style={{ flex: 1, position: "relative", height: "100%" }}>
                                    <AvanceProyectosChartBarras data={filteredData} />
                                </div>
                            </div>
                        </div>
                        <div
                            className="col-12 col-md-6 d-flex flex-column mb-5 pt-2"
                            style={{
                                flex: 1,
                                minHeight: "500px",
                                width: "100%",
                                maxWidth: "600px",
                                margin: "auto",
                                background: "#fff",
                                borderRadius: "12px",
                                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                transition: "box-shadow 0.3s ease, transform 0.2s ease",
                                overflow: "hidden",
                                cursor: "pointer",
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.boxShadow = "0px 8px 16px rgba(0, 0, 0, 0.2)";
                                e.currentTarget.style.transform = "translateY(-5px)";
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.boxShadow = "0px 4px 8px rgba(0, 0, 0, 0.1)";
                                e.currentTarget.style.transform = "translateY(0px)";
                            }}
                        >
                            <Button type="primary" onClick={() => handleOpenModal("radar")} style={{ padding: "5px" }}>
                                Maximizar
                            </Button>
                            <AvanceProyectosChart data={filteredData} />
                        </div>
                    </div>
                </div>
            ) : null}
            <Divider />
            <div
                ref={tableRef}
                style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    overflowX: "auto"
                }}
            >
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    pagination={false}
                    style={{
                        marginBottom: "2em",
                        width: mostrarFechas ? "100%" : "60%",
                        maxWidth: "100%",
                        minWidth: "600px",
                        transition: "width 0.3s ease-in-out",
                    }}
                />
            </div>
            <Modal
                title="Porcentajes de Avance POA´s"
                open={visibleModal}
                onCancel={() => setVisibleModal(false)}
                footer={null}
                width={"80vw"}
                style={{ top: 20 }}
            >
                <div
                    style={{
                        width: "100%",
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "stretch",
                        padding: "20px"
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            height: "100%",
                            width: "100%",
                        }}
                    >
                        {selectedChart === "barras" ? (
                            <AvanceProyectosChartBarras data={filteredData} />
                        ) : selectedChart === "radar" ? (
                            <AvanceProyectosChart data={filteredData} />
                        ) : null}
                    </div>
                </div>
            </Modal>
            <Modal
                title={`Detalles del Proyecto: ${proyectoSeleccionado?.codigo || ""} - ${proyectoSeleccionado?.nombreProyecto || ""}`}
                open={visible}
                onCancel={cerrarModal}
                modalStyle={{ top: 30 }}
                style={{ top: 30 }}
                footer={[
                    <Button key="close" onClick={cerrarModal}>
                        Cerrar
                    </Button>,
                ]}
                width={'80%'}
            >
                <div>
                    <Card style={{ background: "#f5f5f5", borderRadius: 10, padding: "15px" }}>
                        <Row gutter={[16, 8]}>
                            <Col span={24}>
                                <Badge.Ribbon text="Inicio" color="blue">
                                    <Card size="small">
                                        <Text strong>Fecha Inicio POA: </Text>
                                        {proyectoSeleccionado?.fecha_inicio || "N/A"}
                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                            <Col span={24}>
                                <Badge.Ribbon text="Última actualización" color="green">
                                    <Card size="small">
                                        <Text strong>Fecha de Corte de Avances: </Text>
                                        {proyectoSeleccionado?.fecha_corte || "N/A"}
                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                            <Col span={24}>
                                <Badge.Ribbon text="Última actualización" color="green">
                                    <Card size="small">
                                        <Text strong>Fecha Inicial de Terminación POA: </Text>
                                        {proyectoSeleccionado?.fecha_terminacion || "N/A"}
                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                            <Col span={24}>
                                <Badge.Ribbon text="Terminación" color="red">
                                    <Card size="small">
                                        <Text strong>Fecha Actual de Terminación POA: </Text>
                                        {!proyectoSeleccionado?.fecha_actual_terminacion ||
                                            proyectoSeleccionado.fecha_actual_terminacion === "0000-00-00"
                                            ? "N/A"
                                            : proyectoSeleccionado.fecha_actual_terminacion}
                                    </Card>
                                </Badge.Ribbon>
                            </Col>
                        </Row>
                    </Card>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 16 }}>
                    <Space size="large">
                        {conversionData.map((item) => (
                            <Badge
                                key={item.rango}
                                color={item.color}
                                text={`${item.rango} (${item.descripcion})`}
                            />
                        ))}
                    </Space>
                </div>
                <Divider />
                <Table
                    columns={columnasModal}
                    dataSource={proyectoSeleccionado?.historialAvances || []}
                    rowKey={(record) => `${record.mes}-${record.ano}`}
                    rowClassName={(record) =>
                        record.mes === proyectoSeleccionado?.mes && record.ano === proyectoSeleccionado?.ano
                            ? "resaltado"
                            : ""
                    }
                    pagination={false}
                />
            </Modal>
        </div>
    );
};

export default PoaResumen;
